import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTowerBroadcast } from '@fortawesome/pro-light-svg-icons/faTowerBroadcast';
import { SocialPostMedia } from 'fym-shared/src/music24/socialPostFormat';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelHead } from '../../components/primitives/Panel';
import { ChannelHandleForm } from '../channels/ChannelHandleForm';
import { platformMeta } from '../platformMeta';
import { ScheduledPostFormat, SerializedSocialChannel, SocialPlatform } from '../types';
import { ComposerChannelChips } from './ComposerChannelChips';
import { ComposerFormatLines } from './ComposerFormatLines';
import { composerPostingModes } from './composerPostingModes';
import { ComposerPostingModesLine } from './ComposerPostingModesLine';
import { ChannelXPosts } from '../channels/ChannelXPosts';
import { isXPostsFull } from '../xPostsLimit';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerChannelsPanelProps {
  channels: SerializedSocialChannel[];
  selected: SocialPlatform[];
  connecting?: SocialPlatform;
  /** Platforms this post's format cannot reach, such as a story on TikTok. */
  disabledPlatforms?: SocialPlatform[];
  /** The chosen platforms among them. Only these get a "Cannot go out" format line. */
  blockedSelectedPlatforms?: SocialPlatform[];
  /** Selected platforms that cannot post text only and have no media to post. */
  needsMediaPlatforms?: SocialPlatform[];
  /** Platforms that cannot take the attached media (or no media). */
  unavailablePlatforms?: SocialPlatform[];
  /** Unavailable platforms that take this media as a one-off publish (a full-song video). */
  oneOffPlatforms?: SocialPlatform[];
  /** The X account used its posts of this month and the post has a time in it (design c2). */
  isXMonthBlocked?: boolean;
  /** Picked platforms that the last media change took off the post. */
  droppedPlatforms?: SocialPlatform[];
  /** The attached media in the shape of the format rule. Null: the post has no media. */
  media: SocialPostMedia | null;
  /** True while the browser measures the attached video. */
  isMeasuring: boolean;
  /** A feed post or a story. */
  format: ScheduledPostFormat;
  isSaving: boolean;
  onToggle: (platform: SocialPlatform) => void;
  onConnect: (platform: SocialPlatform) => void;
  onCancelConnect: () => void;
  onSaveHandle: (handle: string) => void;
}

/** Which channels the post goes to. The media above decides which ones can take it. */
const ComposerChannelsPanelComponent: React.FC<ComposerChannelsPanelProps> = ({
  channels,
  selected,
  connecting,
  disabledPlatforms,
  blockedSelectedPlatforms,
  needsMediaPlatforms,
  unavailablePlatforms,
  oneOffPlatforms,
  isXMonthBlocked = false,
  droppedPlatforms,
  media,
  isMeasuring,
  format,
  isSaving,
  onToggle,
  onConnect,
  onCancelConnect,
  onSaveHandle,
}) => {
  const intl = useIntl();
  // The X count under the chips when the month is used (design c2): why the chip is grey, or that
  // the X post can wait as a draft.
  const xPosts = useMemo(() => {
    const channel = channels.find((candidate) => candidate.platform === 'x');
    return channel?.status === 'connected' && isXPostsFull(channel.xPosts) ? channel.xPosts : null;
  }, [channels]);
  const showsXPosts = xPosts !== null && (isXMonthBlocked || selected.includes('x'));
  // QA finding B11: say which picked channels post by themselves and which come as reminders.
  const postingModes = useMemo(() => composerPostingModes(channels, selected), [channels, selected]);

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faTowerBroadcast} />}
        title={intl.formatMessage({ id: 'app.sml.promote.composer.channels', defaultMessage: 'Channels' })}
        right={intl.formatMessage(
          { id: 'app.sml.promote.composer.channels.selected', defaultMessage: '{count} selected' },
          { count: selected.length }
        )}
      />
      <div className={styles.copyBody}>
        <ComposerChannelChips
          channels={channels}
          selected={selected}
          connecting={connecting}
          disabledPlatforms={disabledPlatforms}
          needsMediaPlatforms={needsMediaPlatforms}
          unavailablePlatforms={unavailablePlatforms}
          oneOffPlatforms={oneOffPlatforms}
          isXMonthBlocked={isXMonthBlocked}
          onToggle={onToggle}
          onConnect={onConnect}
        />
        <ComposerPostingModesLine modes={postingModes} />
        {showsXPosts && xPosts !== null ? (
          <ChannelXPosts usage={xPosts} fullNote={isXMonthBlocked ? 'none' : 'draft'} hideFullLine={!isXMonthBlocked} />
        ) : null}
        {droppedPlatforms !== undefined && droppedPlatforms.length > 0 ? (
          <p className={styles.aiHint} role="status" data-testid="composer-channels-dropped">
            {intl.formatMessage(
              {
                id: 'app.sml.promote.composer.channels.dropped',
                defaultMessage: '{channels} cannot take this media, so we took them off the post.',
              },
              { channels: intl.formatList(droppedPlatforms.map((platform) => platformMeta[platform].name)) }
            )}
          </p>
        ) : null}
        <ComposerFormatLines
          channels={channels}
          platforms={selected}
          blockedPlatforms={blockedSelectedPlatforms}
          media={media}
          isMeasuring={isMeasuring}
          format={format}
        />
        {connecting !== undefined ? (
          <div className={styles.chipForm}>
            <span className={styles.fieldLabel}>
              {intl.formatMessage(
                { id: 'app.sml.promote.channel.handle.label', defaultMessage: '{platform} handle' },
                { platform: platformMeta[connecting].name }
              )}
            </span>
            <ChannelHandleForm
              meta={platformMeta[connecting]}
              initialHandle={null}
              isSaving={isSaving}
              onSave={onSaveHandle}
              onCancel={onCancelConnect}
            />
          </div>
        ) : null}
      </div>
    </Panel>
  );
};

export const ComposerChannelsPanel = TypedMemo(ComposerChannelsPanelComponent);
